import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, switchMap, take, throttleTime } from 'rxjs/operators';
import { ConfigurationFacade } from '../facades/configuration.facade';
import { SsoAuthService } from '../services/auth/sso-auth.service';

export enum LoginStatus {
  Ok = 'Ok',
  UnAuthorized = 'UNAUTHORIZED',
  UnAuthorizedInactive = 'UNAUTHORIZED-INACTIVE',
  FailedToEndLogin = 'FAILED_TO_END_LOGIN',
}

// Marks a request that has already been retried once after a token refresh, so we don't retry it again
const RETRIED_AFTER_REFRESH_HEADER = 'X-Auth-Retried';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private throttleLogout = new Subject<LoginStatus>();
  private ssoAuthService = inject(SsoAuthService);
  private configurationFacade = inject(ConfigurationFacade);

  private isRefreshingToken = false;
  // Emits once a refresh attempt settles, so requests that arrive while it's in flight can wait for the same result
  private refreshTokenResult$ = new Subject<boolean>();

  constructor(private router: Router) {
    this.setupThrottledLogout();
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((response: HttpErrorResponse) => {
        if (response.status !== 401) {
          return throwError(() => response);
        }

        if (this.isInactiveUser(response) || request.headers.has(RETRIED_AFTER_REFRESH_HEADER)) {
          this.notifyLogout(
            this.isInactiveUser(response) ? LoginStatus.UnAuthorizedInactive : LoginStatus.UnAuthorized,
          );
          return throwError(() => response);
        }

        return this.handleUnauthorized(request, next);
      }),
    );
  }

  // Refreshes the token (once, even if several requests fail at the same time) and retries the failed request
  private handleUnauthorized(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshingToken) {
      this.isRefreshingToken = true;

      return this.ssoAuthService.refreshToken().pipe(
        switchMap(() => {
          this.isRefreshingToken = false;
          this.refreshTokenResult$.next(true);
          return next.handle(this.retryWithFreshCsrf(request));
        }),
        catchError(() => {
          this.isRefreshingToken = false;
          this.refreshTokenResult$.next(false);
          this.notifyLogout(LoginStatus.UnAuthorized);
          return throwError(() => new HttpErrorResponse({ status: 401, url: request.url }));
        }),
      );
    }

    // A refresh is already in progress for another request - wait for it instead of starting a new one
    return this.refreshTokenResult$.pipe(
      take(1),
      switchMap((refreshSucceeded) => {
        if (!refreshSucceeded) {
          return throwError(() => new HttpErrorResponse({ status: 401, url: request.url }));
        }
        return next.handle(this.retryWithFreshCsrf(request));
      }),
    );
  }

  private retryWithFreshCsrf(request: HttpRequest<any>): HttpRequest<any> {
    const csrfTokenName = this.configurationFacade.getValueFor('blauth').csrfTokenName;
    const csrfToken = this.ssoAuthService.getCsrfToken() ?? '';

    return request.clone({
      headers: request.headers.set(csrfTokenName, csrfToken).set(RETRIED_AFTER_REFRESH_HEADER, '1'),
    });
  }

  private isInactiveUser(response: HttpErrorResponse) {
    return response.error?.message?.toLowerCase() === 'inactive user';
  }

  private notifyLogout(status: LoginStatus) {
    this.throttleLogout.next(status);
  }

  private setupThrottledLogout() {
    this.throttleLogout.pipe(throttleTime(5000)).subscribe((param) => {
      this.logout(param);
    });
  }

  private logout(error: string) {
    this.router.navigate(['login'], { queryParams: { error } });
  }
}
